import Button from '@/components/Button'
import Text from '@/components/Text'
import { defaultText, grayBg, grayBorder, leastText, primary, secondaryText, warning } from '@/constant'
import { NotificationDetail } from '@/types'
import AssessmentIcon from '@mui/icons-material/Assessment'
import CloseIcon from '@mui/icons-material/Close'
import CommentIcon from '@mui/icons-material/Comment'
import DescriptionIcon from '@mui/icons-material/Description'
import EventIcon from '@mui/icons-material/Event'
import LanguageIcon from '@mui/icons-material/Language'
import LinkIcon from '@mui/icons-material/Link'
import PersonIcon from '@mui/icons-material/Person'
import ScheduleIcon from '@mui/icons-material/Schedule'
import TitleIcon from '@mui/icons-material/Title'
import { Box, Chip, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Button as MuiButton, Stack } from '@mui/material'
import { SnackbarContent, SnackbarKey, useSnackbar } from 'notistack'
import React, { useCallback, useState } from 'react'
import { NotificationCard, NotificationIcon, Row } from './components'

export interface CustomNotificationProps {
    message: NotificationDetail;
    id: SnackbarKey;
}

const ForwardCompoennt = (
    props: CustomNotificationProps,
    ref: React.Ref<HTMLDivElement>
) => {
    const { message, id } = props
    const { closeSnackbar } = useSnackbar()
    const [detailOpen, setDetailOpen] = useState(false)

    const handleClose = useCallback(
        (id: SnackbarKey) => () => {
            closeSnackbar(id)
        },
        [closeSnackbar]
    )

    const handleViewDetail = useCallback(() => {
        setDetailOpen(true)
    }, [])

    const handleCloseDetail = useCallback(() => {
        setDetailOpen(false)
        closeSnackbar(id)
    }, [closeSnackbar, id])

    interface FullTextData {
        Title: string
        Comment: string
        Path: string
        AlertTime: string
        Type: string
        Desc: string
        HistoryUUID: string
        RiverHost: string
        OrgId: string
    }

    const renderTextWithLinks = (text: string) => {
        if (!text) return null
        const linkRegex = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)|(https?:\/\/[^\s)]+)/g
        const parts = []
        let lastIndex = 0
        let match

        while ((match = linkRegex.exec(text)) !== null) {
            if (match.index > lastIndex) {
                parts.push(text.substring(lastIndex, match.index))
            }
            if (match[1] && match[2]) {
                // [text](url) 格式
                parts.push(
                    <a key={match.index} href={match[2]} target="_blank" rel="noopener noreferrer" style={{ color: primary, textDecoration: 'none' }}>
                        {match[1]}
                    </a>
                )
            } else if (match[3]) {
                // 纯 URL 格式
                parts.push(
                    <a key={match.index} href={match[3]} target="_blank" rel="noopener noreferrer" style={{ color: primary, textDecoration: 'none' }}>
                        {match[3]}
                    </a>
                )
            }
            lastIndex = linkRegex.lastIndex
        }
        if (lastIndex < text.length) {
            parts.push(text.substring(lastIndex))
        }
        return parts
    }

    // 信息项组件
    const InfoItem = ({
        icon,
        label,
        value,
        valueSx = {},
    }: {
        icon?: React.ReactNode
        label: string
        value: React.ReactNode
        valueSx?: object
    }) => (
        <Stack direction="row" alignItems="flex-start" spacing={1}>
            {icon && <Box sx={{ color: primary, display: 'flex', alignItems: 'center', fontSize: '16px', mt: '3px' }}>{icon}</Box>}
            <Box sx={{ flex: 1, wordBreak: 'break-word', fontSize: '13px', lineHeight: '1.6' }}>
                <Text size="xs" sx={{ color: secondaryText, display: 'inline' }}>{label}：</Text>
                {typeof value === 'string' ? (
                    <Text size="xs" sx={{ color: defaultText, display: 'inline', ...valueSx }}>{renderTextWithLinks(value)}</Text>
                ) : (
                    <Box component="span" sx={{ display: 'inline-flex', verticalAlign: 'middle', ...valueSx }}>
                        {value}
                    </Box>
                )}
            </Box>
        </Stack>
    )

    const renderContent = (content: string) => {
        if (!content) return null

        try {
            const parsed: FullTextData = JSON.parse(content)

            // 检查是否是网站检测服务的消息格式
            if (parsed.Title || parsed.Type || parsed.AlertTime) {
                const detailUrl = parsed.RiverHost && parsed.HistoryUUID && parsed.OrgId
                    ? `${parsed.RiverHost}/console/app/radar-${parsed.OrgId}/home?message_id=${parsed.HistoryUUID}`
                    : null

                return (
                    <Stack spacing={2} sx={{ p: 2.5, bgcolor: '#fff' }}>
                        {parsed.Title && (
                            <InfoItem
                                icon={<TitleIcon sx={{ fontSize: '16px' }} />}
                                label="网页标题"
                                value={parsed.Title}
                                valueSx={{ fontWeight: 'bold' }}
                            />
                        )}

                        {parsed.Path && (
                            <InfoItem
                                icon={<LinkIcon sx={{ fontSize: '16px' }} />}
                                label="网页路径"
                                value={parsed.Path}
                                valueSx={{ color: primary }}
                            />
                        )}

                        {parsed.RiverHost && (
                            <InfoItem
                                icon={<LanguageIcon sx={{ fontSize: '16px' }} />}
                                label="详情地址"
                                value={parsed.RiverHost}
                                valueSx={{ color: primary }}
                            />
                        )}

                        {parsed.Comment && (
                            <InfoItem
                                icon={<CommentIcon sx={{ fontSize: '16px' }} />}
                                label="网站备注"
                                value={parsed.Comment}
                            />
                        )}

                        {parsed.Type && (
                            <InfoItem
                                icon={<AssessmentIcon sx={{ fontSize: '16px', color: warning }} />}
                                label="监控指标"
                                value={
                                    <Chip
                                        label={parsed.Type}
                                        size="small"
                                        sx={{
                                            height: '24px',
                                            bgcolor: 'rgba(255, 191, 0, 0.1)',
                                            color: '#B8860B',
                                            fontWeight: 500,
                                            fontSize: '12px',
                                            borderRadius: '4px',
                                        }}
                                    />
                                }
                            />
                        )}

                        {parsed.AlertTime && (
                            <InfoItem
                                icon={<ScheduleIcon sx={{ fontSize: '16px' }} />}
                                label="告警时间"
                                value={parsed.AlertTime}
                            />
                        )}

                        {parsed.Desc && (
                            <InfoItem
                                icon={<DescriptionIcon sx={{ fontSize: '16px' }} />}
                                label="判断依据"
                                value={parsed.Desc}
                                valueSx={{
                                    bgcolor: grayBg,
                                    p: 1.5,
                                    borderRadius: '6px',
                                    mt: 1,
                                    whiteSpace: 'pre-wrap',
                                    display: 'block'
                                }}
                            />
                        )}

                        {/* 查看详情链接 */}
                        {detailUrl && (
                            <Stack direction="row" justifyContent="flex-end" sx={{ pt: 1, mt: 1, borderTop: `1px solid ${grayBorder}` }}>
                                <MuiButton
                                    variant="text"
                                    size="small"
                                    onClick={() => window.open(detailUrl, '_blank')}
                                    sx={{ borderRadius: '6px' }}
                                >
                                    在雷达中查看
                                </MuiButton>
                            </Stack>
                        )}
                    </Stack>
                )
            }

            // 其他 JSON 格式，格式化展示
            return (
                <Box
                    sx={{
                        p: 2.5,
                        bgcolor: '#1e1e1e', // 深色主题适合展示代码
                        boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.1)',
                        overflowX: 'auto',
                    }}
                >
                    <pre style={{
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        fontFamily: '"Fira Code", "Consolas", monospace',
                        fontSize: '13px',
                        color: '#d4d4d4', // 浅灰色字体
                        margin: 0,
                        lineHeight: '1.6',
                    }}>
                        {JSON.stringify(parsed, null, 2)}
                    </pre>
                </Box>
            )
        } catch {
            // 不是 JSON，直接展示原始文本
            let formattedContent = content
            if (typeof formattedContent === 'string' && formattedContent.includes('> - ')) {
                formattedContent = formattedContent
                    .split('> - ')
                    .filter(Boolean)
                    .map((item) => `> - ${item.trim()}`)
                    .join('\n')
            }

            return (
                <Box
                    sx={{
                        p: 3,
                        bgcolor: '#fff',
                    }}
                >
                    <Text
                        size="sm"
                        sx={{
                            color: defaultText,
                            lineHeight: '1.8',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                        }}
                    >
                        {renderTextWithLinks(formattedContent)}
                    </Text>
                </Box>
            )
        }
    }

    return (
        <>
            <SnackbarContent ref={ref} role="alert">
                <NotificationCard>
                    <Row sx={{ justifyContent: 'space-between' }}>
                        <Text
                            weight="bold"
                            size="xs"
                            sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                        >
                            <NotificationIcon />
                            消息
                        </Text>
                        <IconButton sx={{ borderRadius: '4px' }} onClick={handleClose(id)}>
                            <CloseIcon sx={{ fontSize: '16px', color: '#666666' }} />
                        </IconButton>
                    </Row>
                    <Stack sx={{ marginLeft: '32px' }}>
                        <Text weight="regular" size="xs" sx={{ color: secondaryText }}>
                            {message?.simple_text}
                        </Text>
                        <Text weight="regular" size="xs" sx={{ color: leastText }}>
                            {message?.created_at}
                        </Text>
                    </Stack>

                    <Row sx={{ justifyContent: 'flex-end' }}>
                        {message?.full_text && (
                            <Button type="text" onClick={handleViewDetail}>
                                查看详情
                            </Button>
                        )}
                    </Row>
                </NotificationCard>
            </SnackbarContent>

            <Dialog
                open={detailOpen}
                disableEscapeKeyDown
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: '8px',
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        pb: 2,
                        borderBottom: '1px solid #e0e0e0',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}
                >
                    <Text weight="bold" size="sm">
                        消息详情
                    </Text>
                    <IconButton onClick={handleCloseDetail} sx={{ padding: '4px' }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ py: 3, bgcolor: '#FAFBFC' }}>
                    <Stack spacing={3}>
                        {(message?.send_name || message?.created_at) && (
                            <Box>
                                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                                    <Box sx={{ width: 4, height: 16, bgcolor: primary, borderRadius: 1 }} />
                                    <Text weight="bold" size="md" sx={{ color: defaultText }}>
                                        基本信息
                                    </Text>
                                </Stack>
                                <Stack
                                    direction="row"
                                    spacing={4}
                                    sx={{
                                        p: 2,
                                        bgcolor: '#fff',
                                        borderRadius: '8px',
                                        border: `1px solid ${grayBorder}`,
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
                                    }}
                                >
                                    {message?.send_name && (
                                        <Stack direction="row" alignItems="center" spacing={1.5}>
                                            <Box sx={{
                                                width: 32,
                                                height: 32,
                                                borderRadius: '50%',
                                                bgcolor: 'rgba(24, 144, 255, 0.1)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                color: primary
                                            }}>
                                                <PersonIcon sx={{ fontSize: '18px' }} />
                                            </Box>
                                            <Stack spacing={0.2}>
                                                <Text size="xs" sx={{ color: secondaryText, fontSize: '12px' }}>发送方</Text>
                                                <Text size="sm" weight="bold" sx={{ color: defaultText }}>{message.send_name}</Text>
                                            </Stack>
                                        </Stack>
                                    )}
                                    {message?.created_at && (
                                        <Stack direction="row" alignItems="center" spacing={1.5}>
                                            <Box sx={{
                                                width: 32,
                                                height: 32,
                                                borderRadius: '50%',
                                                bgcolor: 'rgba(82, 196, 26, 0.1)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                color: '#52c41a'
                                            }}>
                                                <EventIcon sx={{ fontSize: '18px' }} />
                                            </Box>
                                            <Stack spacing={0.2}>
                                                <Text size="xs" sx={{ color: secondaryText, fontSize: '12px' }}>时间</Text>
                                                <Text size="sm" weight="bold" sx={{ color: defaultText }}>{message.created_at}</Text>
                                            </Stack>
                                        </Stack>
                                    )}
                                </Stack>
                            </Box>
                        )}
                        <Box>
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                                <Box sx={{ width: 4, height: 16, bgcolor: primary, borderRadius: 1 }} />
                                <Text weight="bold" size="md" sx={{ color: defaultText }}>
                                    详细内容
                                </Text>
                            </Stack>
                            <Box sx={{
                                bgcolor: '#fff',
                                borderRadius: '8px',
                                border: `1px solid ${grayBorder}`,
                                boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
                                overflow: 'hidden'
                            }}>
                                {renderContent(message?.full_text || '')}
                            </Box>
                        </Box>
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #e0e0e0' }}>
                    <MuiButton onClick={handleCloseDetail} variant="contained">
                        关闭
                    </MuiButton>
                </DialogActions>
            </Dialog>
        </>
    )
}

const NotificationsComponent = React.forwardRef<
    HTMLDivElement,
    CustomNotificationProps
>(ForwardCompoennt)

export const enqueueNotification = () => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { enqueueSnackbar } = useSnackbar()

    const pushNotification = (message: NotificationDetail) => {
        enqueueSnackbar(message?.simple_text, {
            persist: !!message?.full_text,
            content: (key) => {
                return <NotificationsComponent id={key} message={message} />
            },
        })
    }
    return pushNotification
}

export default NotificationsComponent
